import { resolveLatencyWeight } from './latencyWeighting/resolveLatencyWeight';

export const LATENCY_SOURCE = {
  REQUEST: 'request',
  SERVER_TIMING: 'server-timing',
  CONNECTION: 'connection',
  PROBE: 'probe',
} as const;

export type LatencySource = (typeof LATENCY_SOURCE)[keyof typeof LATENCY_SOURCE];

export interface LatencySample {
  durationMs: number;
  isTimeout: boolean;
  weight: number;
  url?: string;
  networkLatencyMs?: number;
  latencySource?: LatencySource;
  throughputMbps?: number;
}

export interface PublishLatencyInput {
  durationMs: number;
  isTimeout: boolean;
  url?: string;
  networkLatencyMs?: number;
  latencySource?: LatencySource;
  throughputMbps?: number;
}

type LatencyListener = (sample: LatencySample) => void;

const MAX_BUFFERED_SAMPLES = 20;

let samples: LatencySample[] = [];
const listeners = new Set<LatencyListener>();

export const publishLatencySample = (input: PublishLatencyInput): void => {
  const sample: LatencySample = {
    durationMs: input.durationMs,
    isTimeout: input.isTimeout,
    url: input.url,
    weight: resolveLatencyWeight(input.url),
    ...(input.networkLatencyMs !== undefined && {
      networkLatencyMs: input.networkLatencyMs,
    }),
    ...(input.latencySource !== undefined && {
      latencySource: input.latencySource,
    }),
    ...(input.throughputMbps !== undefined && {
      throughputMbps: input.throughputMbps,
    }),
  };

  samples = [...samples, sample].slice(-MAX_BUFFERED_SAMPLES);

  listeners.forEach((listener) => {
    listener(sample);
  });
};

export const subscribeToLatencySamples = (
  listener: LatencyListener,
): (() => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

export const getRecentLatencySamples = (count: number): LatencySample[] => {
  return samples.slice(-count);
};

export const getRecentContributingSamples = (
  count: number,
): LatencySample[] => {
  return samples.filter((sample) => sample.weight > 0).slice(-count);
};

export const getRecentRequestSamples = (count: number): LatencySample[] => {
  return samples
    .filter(
      (sample) =>
        sample.weight > 0 &&
        sample.networkLatencyMs === undefined &&
        sample.throughputMbps === undefined,
    )
    .slice(-count);
};

export const getRecentNetworkSamples = (count: number): LatencySample[] => {
  return samples
    .filter(
      (sample) => sample.weight > 0 && sample.networkLatencyMs !== undefined,
    )
    .slice(-count);
};

export const getRecentThroughputSamples = (count: number): LatencySample[] => {
  return samples
    .filter(
      (sample) => sample.weight > 0 && sample.throughputMbps !== undefined,
    )
    .slice(-count);
};

export const resetLatencyObserver = (): void => {
  samples = [];
  listeners.clear();
};
